// Kaart in het lead-detail: het intakegesprek in zeven stappen met live prijs.
// Het gesprek wordt voorgevuld uit de lead (en de bijbehorende website-aanvraag), de pakketten
// komen uit crm_services, en het plan wordt met één klik als activiteit bij de lead opgeslagen.
// Verder schrijft dit onderdeel niets: geen wijzigingen aan de lead zelf.
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "./supabase";
import {
  BUDGETTEN, GEBRUIKERS, KOPPELINGEN, MAATWERK_SIGNALEN, LEEG,
  bereken, euro, planTekst, SPOED_TOESLAG, GEVOELIG_TOESLAG,
} from "./intake/calc";
import { HULP } from "./intake/hulp";
import { CATEGORIEEN, ZORG_CATEGORIE, STANDAARD_PAKKETTEN, laadCatalogus } from "./intake/pakketten";
import { vanAanvraag, vanLead } from "./intake/prefill";

const C = {
  bg: "#0a0a0c", surface: "#131418", surfaceHigh: "#1c1d23", border: "#2a2c34",
  green: "#34bd82", greenDim: "rgba(52,189,130,0.14)", red: "#e5564e", redDim: "rgba(229,86,78,0.14)",
  yellow: "#e0a82e", yellowDim: "rgba(224,168,46,0.14)", blue: "#5b8eff", blueDim: "rgba(91,142,255,0.14)",
  text: "#f3f4f6", muted: "#a0a4af", dim: "#4a4d57",
};

const STAPPEN = ["Aanvraag", "Doel", "Gebruikers", "Data en wet", "Planning", "Pakket", "Plan"];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const conceptSleutel = (leadId) => "intake_concept_" + leadId;

const Veld = ({ id, label, hulp, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label htmlFor={id} style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>
    {hulp && <p style={{ fontSize: 11, color: C.muted, margin: "0 0 5px" }}>{hulp}</p>}
    {children}
  </div>
);

const optStijl = (aan) => ({
  display: "flex", alignItems: "flex-start", gap: 9, padding: "9px 11px", marginBottom: 6, cursor: "pointer",
  border: "1px solid " + (aan ? C.blue : C.border), background: aan ? C.blueDim : C.bg, borderRadius: 7,
});
const invoerReset = { width: "auto", padding: 0, marginTop: 3, flexShrink: 0 };

function Vink({ id, checked, onChange, children, sub, prijs }) {
  return (
    <label htmlFor={id} style={optStijl(checked)}>
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={invoerReset} />
      <span style={{ flex: 1, display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
        <span>{children}{sub && <small style={{ display: "block", color: C.muted, fontSize: 11, marginTop: 2 }}>{sub}</small>}</span>
        {prijs != null && <b style={{ whiteSpace: "nowrap", fontFamily: "monospace", fontSize: 12 }}>{euro(prijs)}</b>}
      </span>
    </label>
  );
}

function Keuze({ naam, label, hulp, waarde, opties, onChange }) {
  return (
    <fieldset style={{ border: "none", padding: 0, margin: "0 0 14px" }}>
      <legend style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, padding: 0 }}>{label}</legend>
      {hulp && <p style={{ fontSize: 11, color: C.muted, margin: "0 0 5px" }}>{hulp}</p>}
      {opties.map(([w, t]) => (
        <label key={w} htmlFor={naam + "-" + w} style={optStijl(waarde === w)}>
          <input id={naam + "-" + w} type="radio" name={naam} value={w} checked={waarde === w} onChange={() => onChange(w)} style={invoerReset} />
          <span style={{ fontSize: 13 }}>{t}</span>
        </label>
      ))}
    </fieldset>
  );
}

const Kop = ({ children }) => (
  <p style={{ margin: "18px 0 6px", fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", color: C.muted, fontWeight: 700, fontFamily: "monospace" }}>{children}</p>
);

const Melding = ({ soort, children }) => {
  const kleur = soort === "good" ? [C.green, C.greenDim] : soort === "bad" ? [C.red, C.redDim] : [C.yellow, C.yellowDim];
  return <div role="status" style={{ padding: "9px 12px", borderRadius: 7, fontSize: 12, marginBottom: 12, color: kleur[0], background: kleur[1], border: "1px solid " + kleur[0] + "55" }}>{children}</div>;
};

function Gesprek({ lead, onSluit }) {
  const [laden, setLaden] = useState(true);
  const [g, setG] = useState(LEEG);
  const [catalogus, setCatalogus] = useState(STANDAARD_PAKKETTEN);
  const [bron, setBron] = useState("standaard");
  const [herkomst, setHerkomst] = useState("");
  const [uitConcept, setUitConcept] = useState(false);
  const [stap, setStap] = useState(0);
  const [hoogste, setHoogste] = useState(0);
  const [hulp, setHulp] = useState("");
  const [gekopieerd, setGekopieerd] = useState("");
  const [bezig, setBezig] = useState(false);
  const [opslag, setOpslag] = useState(null);

  const voorvullen = async () => {
    const { data: a } = await supabase.from("website_aanvragen").select("*").eq("crm_lead_id", lead.id).order("created_at", { ascending: false }).limit(1).maybeSingle();
    return a ? { g: vanAanvraag(a), herkomst: "Voorgevuld uit de website-aanvraag" } : { g: vanLead(lead), herkomst: "Voorgevuld uit de lead" };
  };

  useEffect(() => {
    let weg = false;
    (async () => {
      const [cat, vul] = await Promise.all([laadCatalogus(supabase), voorvullen()]);
      if (weg) return;
      setCatalogus(cat.pakketten); setBron(cat.bron);
      let concept = null;
      try { concept = JSON.parse(localStorage.getItem(conceptSleutel(lead.id)) || "null"); } catch { concept = null; }
      if (concept && concept.g) {
        setG({ ...LEEG, ...concept.g }); setStap(concept.stap || 0); setHoogste(concept.hoogste || 0); setUitConcept(true);
        setHerkomst("Concept hersteld van een eerder gesprek");
      } else { setG(vul.g); setHerkomst(vul.herkomst); }
      setLaden(false);
    })();
    return () => { weg = true; };
  }, [lead.id]);

  // Concept bewaren in deze browser zodat een per ongeluk gesloten scherm niets kost.
  useEffect(() => {
    if (laden || opslag?.ok) return;
    try { localStorage.setItem(conceptSleutel(lead.id), JSON.stringify({ g, stap, hoogste })); } catch { /* geen opslag beschikbaar */ }
  }, [g, stap, hoogste, laden, opslag, lead.id]);

  useEffect(() => {
    const toets = (e) => { if (e.key === "Escape") onSluit(); };
    window.addEventListener("keydown", toets);
    return () => window.removeEventListener("keydown", toets);
  }, [onSluit]);

  const b = useMemo(() => bereken(g, catalogus), [g, catalogus]);
  const plan = useMemo(() => planTekst(g, b), [g, b]);

  const zet = (k, v) => setG((p) => ({ ...p, [k]: v }));
  const wissel = (k, v) => setG((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));
  const ga = (i) => { if (i < 0 || i >= STAPPEN.length) return; setStap(i); setHoogste((h) => Math.max(h, i)); };

  const gekozenHulp = HULP.find((h) => h.waarde === hulp);
  const neemOver = () => {
    if (!gekozenHulp) return;
    setG((p) => ({ ...p, pakketten: [...p.pakketten, ...gekozenHulp.slugs.filter((s) => catalogus.some((c) => c.slug === s) && !p.pakketten.includes(s))] }));
  };

  const opnieuw = async () => {
    if (!window.confirm("Alle antwoorden wissen en opnieuw beginnen vanuit de lead?")) return;
    try { localStorage.removeItem(conceptSleutel(lead.id)); } catch { /* niets te wissen */ }
    const vul = await voorvullen();
    setG(vul.g); setHerkomst(vul.herkomst); setUitConcept(false); setStap(0); setHoogste(0); setHulp(""); setOpslag(null);
  };

  const kopieer = async () => {
    try { await navigator.clipboard.writeText(plan); setGekopieerd("Gekopieerd"); }
    catch { setGekopieerd("Selecteer het plan en kopieer met Cmd + C"); }
  };

  const opslaan = async () => {
    if (!UUID.test(String(lead.id))) { setOpslag({ ok: false, tekst: "Ongeldige lead." }); return; }
    const tekst = plan.trim();
    if (tekst.length < 20 || tekst.length > 20000) { setOpslag({ ok: false, tekst: "Het plan is te kort of te lang om op te slaan." }); return; }
    setBezig(true);
    const tijd = new Intl.DateTimeFormat("nl-NL", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Amsterdam" }).format(new Date());
    const { error } = await supabase.from("activities").insert([{ lead_id: lead.id, type: "Intakegesprek", icon: "▤", text: tekst, time: tijd }]);
    setBezig(false);
    if (error) {
      console.error("Intake opslaan mislukt", error.code);
      setOpslag({ ok: false, tekst: "Opslaan is niet gelukt. Kopieer het plan en probeer het later opnieuw." });
      return;
    }
    try { localStorage.removeItem(conceptSleutel(lead.id)); } catch { /* niets te wissen */ }
    setOpslag({ ok: true, tekst: "Plan opgeslagen als activiteit bij de lead." });
  };

  const categorieen = [...CATEGORIEEN, "Overig"].filter((c) => catalogus.some((p) => p.categorie === c));
  const zorgPakketten = catalogus.filter((p) => p.categorie === ZORG_CATEGORIE);
  const tekstVeld = (id, k, ...rest) => <input id={id} type="text" value={g[k]} onChange={(e) => zet(k, e.target.value)} {...rest[0]} />;

  const stappen = [
    <>
      <p style={{ color: C.muted, fontSize: 12, marginTop: 0 }}>Wie is het en wat kwam er binnen? Dit is vooraf ingevuld vanuit de CRM. Pas aan waar nodig.</p>
      <Veld id="in-bedrijf" label="Bedrijf">{tekstVeld("in-bedrijf", "bedrijf")}</Veld>
      <Veld id="in-contact" label="Contactpersoon">{tekstVeld("in-contact", "contact")}</Veld>
      <Veld id="in-contactinfo" label="E-mail of telefoon">{tekstVeld("in-contactinfo", "contactinfo")}</Veld>
      <Veld id="in-branche" label="Branche">{tekstVeld("in-branche", "branche")}</Veld>
      <Veld id="in-aanvraag" label="Wat vraagt de klant?" hulp="Eigen woorden van de klant, zo letterlijk mogelijk.">
        <textarea id="in-aanvraag" rows={5} value={g.aanvraag} onChange={(e) => zet("aanvraag", e.target.value)} />
      </Veld>
    </>,
    <>
      <p style={{ color: C.muted, fontSize: 12, marginTop: 0 }}>Wat moet het opleveren, en hoe merken we dat het gelukt is?</p>
      <Veld id="in-doel" label="Wat wil de klant bereiken?"><textarea id="in-doel" rows={3} value={g.doel} onChange={(e) => zet("doel", e.target.value)} /></Veld>
      <Veld id="in-succes" label="Hoe merken ze dat het gelukt is?" hulp="Een getal of datum helpt, bijvoorbeeld bestellingen per week.">{tekstVeld("in-succes", "succes")}</Veld>
      <Veld id="in-situatie" label="Hoe gaat het nu, en wat kost dat aan tijd?" hulp="Welke taken of processen, hoeveel uur per week."><textarea id="in-situatie" rows={3} value={g.situatie} onChange={(e) => zet("situatie", e.target.value)} /></Veld>
    </>,
    <>
      <p style={{ color: C.muted, fontSize: 12, marginTop: 0 }}>Wie werkt ermee, en waar moet het mee koppelen? Elke koppeling is € 500 tot € 1.500 extra.</p>
      <Veld id="in-gebruikers" label="Hoeveel mensen gaan het gebruiken?">
        <select id="in-gebruikers" value={g.gebruikers} onChange={(e) => zet("gebruikers", e.target.value)}>
          {GEBRUIKERS.map(([w, t]) => <option key={w} value={w}>{t}</option>)}
        </select>
      </Veld>
      <Veld id="in-rollen" label="Welke rollen zijn er?" hulp="Bijvoorbeeld eigenaar, medewerker, klant.">{tekstVeld("in-rollen", "rollen")}</Veld>
      <Kop>Koppelingen met andere systemen</Kop>
      {KOPPELINGEN.map(([w, t]) => <Vink key={w} id={"in-k-" + w} checked={g.koppelingen.includes(w)} onChange={() => wissel("koppelingen", w)}>{t}</Vink>)}
      <div style={{ height: 12 }} />
      <Veld id="in-bestaand" label="Is er al een website, domein, huisstijl of software?">{tekstVeld("in-bestaand", "bestaand")}</Veld>
    </>,
    <>
      <p style={{ color: C.muted, fontSize: 12, marginTop: 0 }}>Bepaalt de verwerkersovereenkomst en of er 15% toeslag geldt.</p>
      <Keuze naam="in-pers" label="Komen er persoonsgegevens in voor?" waarde={g.pers} opties={[["ja", "Ja"], ["nee", "Nee"]]} onChange={(v) => zet("pers", v)} />
      <Vink id="in-gevoelig" checked={g.gevoelig} onChange={(v) => zet("gevoelig", v)} sub="15% toeslag op de bouwprijs.">Gevoelige data (zorg of financieel)</Vink>
      <Vink id="in-vo" checked={g.vo} onChange={(v) => zet("vo", v)}>Verwerkersovereenkomst is besproken en geregeld</Vink>
      <div style={{ height: 16 }} />
      <Keuze naam="in-ai" label="Verstuurt, publiceert of beslist de oplossing iets namens anderen?" hulp="Elke zo'n AI-actie krijgt een goedkeuringsstap door een mens." waarde={g.ai} opties={[["ja", "Ja, met goedkeuring"], ["nee", "Nee"]]} onChange={(v) => zet("ai", v)} />
      <Vink id="in-zonderControle" checked={g.zonderControle} onChange={(v) => zet("zonderControle", v)} sub="Rode vlag. Dit doen we niet.">Klant wil dit zonder menselijke controle</Vink>
    </>,
    <>
      <p style={{ color: C.muted, fontSize: 12, marginTop: 0 }}>Wanneer moet het klaar zijn, en wat mag het ongeveer kosten?</p>
      <Veld id="in-opleverdatum" label="Gewenste opleverdatum en waarom">{tekstVeld("in-opleverdatum", "opleverdatum")}</Veld>
      <Vink id="in-spoed" checked={g.spoed} onChange={(v) => zet("spoed", v)} sub="25% toeslag op de bouwprijs.">Spoed: oplevering in de helft van de normale tijd</Vink>
      <div style={{ height: 14 }} />
      <Veld id="in-budget" label="Budget, ruwweg">
        <select id="in-budget" value={g.budget} onChange={(e) => zet("budget", e.target.value)}>
          {BUDGETTEN.map((x) => <option key={x.waarde} value={x.waarde}>{x.label}</option>)}
        </select>
      </Veld>
      <Veld id="in-beslisser" label="Wie beslist en tekent?">{tekstVeld("in-beslisser", "beslisser")}</Veld>
      <Vink id="in-korting" checked={g.kortingGevraagd} onChange={(v) => zet("kortingGevraagd", v)} sub="Rode vlag. Eerst overleggen.">Klant vraagt om korting onder het minimum of betaling pas achteraf</Vink>
    </>,
    <>
      <p style={{ color: C.muted, fontSize: 12, marginTop: 0 }}>Kies de pakketten die bij het gesprek passen. De prijs rechts rekent mee.</p>
      {bron === "standaard" && <Melding soort="warn">De prijzen komen uit de standaardlijst. Ze staan nog niet in crm_services, of die kon niet worden gelezen.</Melding>}
      <Veld id="in-hulp" label="Wat zegt de klant?" hulp="De beslishulp stelt een pakket voor.">
        <select id="in-hulp" value={hulp} onChange={(e) => setHulp(e.target.value)}>
          <option value="">Kies wat de klant zegt</option>
          {HULP.map((h) => <option key={h.waarde} value={h.waarde}>{h.label}</option>)}
        </select>
        {gekozenHulp && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, fontSize: 12, color: C.muted }}>
            <span style={{ flex: 1 }}>{gekozenHulp.tekst}</span>
            <button type="button" className="btn bg" onClick={neemOver}>Neem over</button>
          </div>
        )}
      </Veld>
      {categorieen.map((c) => (
        <div key={c}>
          <Kop>{c}</Kop>
          {catalogus.filter((p) => p.categorie === c).map((p) => (
            <Vink key={p.slug} id={"in-p-" + p.slug} checked={g.pakketten.includes(p.slug)} onChange={() => wissel("pakketten", p.slug)} sub={p.omschrijving} prijs={p.prijs}>{p.naam}</Vink>
          ))}
        </div>
      ))}
      <Kop>Doorlopende zorg per maand</Kop>
      <Keuze naam="in-zorg" label="Zorgpakket" waarde={g.zorg}
        opties={[["geen", "Geen"], ...zorgPakketten.map((p) => [p.slug.replace("pakket-zorg-", ""), p.naam + " · " + euro(p.prijs)])]}
        onChange={(v) => zet("zorg", v)} />
      <Kop>Maatwerk-signalen</Kop>
      {MAATWERK_SIGNALEN.map((m, i) => <Vink key={i} id={"in-mw-" + i} checked={g.maatwerk.includes(String(i))} onChange={() => wissel("maatwerk", String(i))}>{m}</Vink>)}
    </>,
    <>
      <p style={{ color: C.muted, fontSize: 12, marginTop: 0 }}>Dit is het plan uit het gesprek. Sla het op bij de lead of kopieer het.</p>
      {opslag && <Melding soort={opslag.ok ? "good" : "bad"}>{opslag.tekst}</Melding>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <button className="btn bp" type="button" onClick={opslaan} disabled={bezig || opslag?.ok === true}>
          {bezig ? "Opslaan..." : opslag?.ok ? "Opgeslagen" : "Opslaan bij de lead"}
        </button>
        <button className="btn bg" type="button" onClick={kopieer}>Kopieer plan</button>
        <span style={{ fontSize: 11, color: C.muted }} role="status">{gekopieerd}</span>
      </div>
      <pre style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 7, padding: 13, fontSize: 12, lineHeight: 1.65, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, fontFamily: "inherit", userSelect: "text" }}>{plan}</pre>
    </>,
  ];

  const regel = (k, links, rechts, extra) => (
    <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", fontSize: 12, borderTop: extra ? "1px solid " + C.border : "none", fontWeight: extra ? 700 : 400 }}>
      <span>{links}</span><span style={{ fontFamily: "monospace", textAlign: "right" }}>{rechts}</span>
    </div>
  );

  return (
    <div className="mdl" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 1080, padding: 0, background: C.surface }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: "1px solid " + C.border }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ fontFamily: "monospace", fontSize: 14, margin: 0 }}>INTAKEGESPREK</h3>
          <p style={{ fontSize: 11, color: C.muted, margin: "3px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {lead.name}{lead.company ? " (" + lead.company + ")" : ""} · {herkomst || "laden..."}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {uitConcept && <button className="btn bg" type="button" onClick={opnieuw}>Opnieuw beginnen</button>}
          <button className="btn bg" type="button" onClick={onSluit} aria-label="Sluiten">Sluiten</button>
        </div>
      </div>
      {laden ? <p style={{ padding: 24, color: C.muted, fontSize: 13 }}>Gesprek voorbereiden...</p> : (
        <div style={{ padding: 18 }}>
          <nav aria-label="Stappen" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {STAPPEN.map((s, i) => {
              const klaar = i < stap && i <= hoogste;
              return (
                <button key={s} type="button" onClick={() => ga(i)} aria-current={i === stap ? "step" : undefined}
                  style={{ padding: "6px 11px", borderRadius: 6, cursor: "pointer", fontSize: 12, border: "1px solid " + (i === stap ? C.blue : C.border), background: i === stap ? C.blueDim : "transparent", color: i === stap ? C.blue : klaar ? C.green : C.muted }}>
                  {klaar ? "✓" : i + 1} {s}
                </button>
              );
            })}
          </nav>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
            <section style={{ flex: "2 1 420px", minWidth: 0 }}>
              <h2 style={{ fontFamily: "monospace", fontSize: 15, margin: "0 0 8px" }}>{STAPPEN[stap]}</h2>
              {stappen[stap]}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }}>
                <button className="btn bg" type="button" onClick={() => ga(stap - 1)} style={{ visibility: stap === 0 ? "hidden" : "visible" }}>Vorige</button>
                <button className="btn bp" type="button" onClick={() => ga(stap + 1)} style={{ visibility: stap === STAPPEN.length - 1 ? "hidden" : "visible" }}>Volgende</button>
              </div>
            </section>
            <aside aria-live="polite" style={{ flex: "1 1 280px", minWidth: 0, background: C.bg, border: "1px solid " + C.border, borderRadius: 8, padding: 14 }}>
              <p className="lbl" style={{ marginBottom: 8 }}>Live overzicht</p>
              {b.gekozen.length ? b.gekozen.map((p) => regel(p.slug, p.naam, euro(p.prijs))) : <p style={{ fontSize: 12, color: C.dim, margin: "4px 0" }}>Nog geen pakket gekozen.</p>}
              {b.gekozen.length > 0 && (
                <>
                  {g.spoed && regel("spoed", "Spoed 25%", euro(b.basis * SPOED_TOESLAG))}
                  {g.gevoelig && regel("gevoelig", "Gevoelige data 15%", euro(b.basis * GEVOELIG_TOESLAG))}
                  {b.koppelingen > 0 && regel("koppel", b.koppelingen + (b.koppelingen > 1 ? " koppelingen" : " koppeling"), euro(b.kLaag) + " tot " + euro(b.kHoog))}
                  {regel("totaal", "Totaal", b.laag === b.hoog ? euro(b.laag) : euro(b.laag) + " tot " + euro(b.hoog), true)}
                  {regel("min", <span style={{ color: C.muted }}>Minimumprijs</span>, euro(b.ondergrens))}
                  {regel("uren", <span style={{ color: C.muted }}>Uren-budget</span>, b.uren + " u")}
                </>
              )}
              {b.zorg.prijs > 0 && regel("zorg", b.zorg.naam, euro(b.zorg.prijs) + "/mnd")}
              <p style={{ fontSize: 11, color: C.muted, margin: "8px 0" }}>Alle prijzen excl. btw.</p>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {b.vlaggen.length
                  ? b.vlaggen.map((f) => <li key={f} style={{ fontSize: 12, color: C.red, background: C.redDim, borderRadius: 6, padding: "6px 9px", marginTop: 6 }}>{f}</li>)
                  : <li style={{ fontSize: 12, color: C.green }}>Geen aandachtspunten.</li>}
              </ul>
            </aside>
          </div>
        </div>
      )}
    </div>
  );
}

export default function IntakeGesprek({ lead }) {
  const [open, setOpen] = useState(false);
  const [heeftConcept, setHeeftConcept] = useState(false);

  useEffect(() => {
    try { setHeeftConcept(!!localStorage.getItem(conceptSleutel(lead.id))); } catch { setHeeftConcept(false); }
  }, [lead.id, open]);

  return (
    <div className="card" style={{ marginBottom: 13 }}>
      <p className="lbl" style={{ marginBottom: 8 }}>Intakegesprek</p>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px", lineHeight: 1.6 }}>
        Voer het gesprek in zeven stappen, met de gegevens van deze lead al ingevuld. De prijs rekent live mee en het plan sla je op als activiteit.
      </p>
      <button className="btn bp" onClick={() => setOpen(true)}>{heeftConcept ? "→ Gesprek hervatten" : "→ Start intakegesprek"}</button>
      {open && createPortal(<div className="ovl" onClick={() => setOpen(false)}><Gesprek lead={lead} onSluit={() => setOpen(false)} /></div>, document.body)}
    </div>
  );
}
